import { Routing } from '@enums/routing.enum';
import { ModuleId } from './module-access.config';

/**
 * Single source of truth for the CMS main navigation.
 *
 * BOTH the desktop sidebar (`SidenavComponent`) and the mobile header
 * (`HeaderComponent` — swipe pill + "More" drawer) render THIS array. Adding a
 * new tab therefore means adding ONE entry here (plus the route in
 * `app.routes.ts`, which Angular keeps separate) and it appears on every surface
 * automatically — no more desktop/mobile drift.
 */
export interface NavItem {
	/** Route path with leading slash. Derived from the `Routing` enum so nav ↔ route can't diverge. */
	path: string;
	/** Material icon name. */
	icon: string;
	/** Raw fallback label (used when `labelKey` is absent). */
	label: string;
	/** i18n key (ngx-translate); takes precedence over `label` when set. */
	labelKey?: string;
	/**
	 * URL prefix used for active-state matching on mobile (where the active pill is
	 * tracked manually). Defaults to `path`. Match is boundary-aware (`/monitor`
	 * won't light up on `/monitoring/...`).
	 */
	prefix?: string;
	/** Only visible to admins. */
	adminOnly?: boolean;
	/**
	 * Inna nazwa i ikona dla zwykłego użytkownika — ta sama trasa pokazuje mu inną treść
	 * (Doradca: admin widzi doradcę automatycznego, użytkownik tylko „Moje transakcje").
	 */
	userLabelKey?: string;
	userIcon?: string;
	/** Renders an unread-count badge on the icon (Investing alerts or Notifications). */
	badge?: 'investingAlerts' | 'notifications';
	/**
	 * Moduł, do którego należy zakładka. Bez dostępu (`canAccessModule`) zakładka jest
	 * zablokowana, a `moduleGuard` przekierowuje na ekran odblokowania.
	 * Brak = strona otwarta dla każdego zalogowanego.
	 */
	module?: ModuleId;
}

/**
 * Zakładki menu (bez adminowych) w wersji dla danej roli — u zwykłego użytkownika
 * podmienia nazwę i ikonę tam, gdzie zakładka ma wariant użytkownika (`userLabelKey`).
 */
export function navItemsForRole(role: string | undefined): NavItem[] {
	const admin = role === 'admin';
	return CMS_NAV_ITEMS.filter((i) => !i.adminOnly).map((i) =>
		!admin && i.userLabelKey
			? { ...i, labelKey: i.userLabelKey, icon: i.userIcon ?? i.icon }
			: i,
	);
}

/** `/path` helper from an enum value. */
const p = (r: Routing): string => `/${r}`;

export const CMS_NAV_ITEMS: NavItem[] = [
	{ path: p(Routing.PanelDashboard), icon: 'dashboard', label: 'Dashboard', module: 'dashboard' },
	{
		path: p(Routing.PanelNotifications),
		icon: 'notifications',
		label: 'Powiadomienia',
		labelKey: 'notifications.nav',
		prefix: '/notifications',
		badge: 'notifications',
		module: 'notifications',
	},
	{
		path: p(Routing.PanelCurrencyList),
		icon: 'currency_bitcoin',
		label: 'Crypto',
		prefix: '/cryptocurrency',
		module: 'cryptolist',
	},
	{ path: p(Routing.PanelSentiment), icon: 'insights', label: 'Sentiment', module: 'sentiment' },
	{
		path: p(Routing.PanelLiquidity),
		icon: 'water_drop',
		label: 'Liquidity',
		labelKey: 'liquidity.nav',
		module: 'volume',
	},
	{
		path: p(Routing.PanelVolumetric),
		icon: 'bar_chart',
		label: 'Volumetric',
		labelKey: 'volumetric.nav',
		prefix: '/volumetric',
		module: 'volumetric',
	},
	{
		path: p(Routing.PanelTrend),
		icon: 'trending_down',
		label: 'Trend',
		labelKey: 'trend.nav',
		module: 'sentiment',
	},
	{
		path: p(Routing.PanelMarketBottoms),
		icon: 'vertical_align_bottom',
		label: 'Dna rynku',
		labelKey: 'nav.marketBottoms',
		prefix: '/market-bottoms',
		module: 'market_bottoms',
	},
	{
		path: p(Routing.PanelTrendLines),
		icon: 'timeline',
		label: 'Linie trendu',
		labelKey: 'trendLines.nav',
		module: 'trendlines',
	},
	{
		// Doradca alertów: gdzie i kiedy ustawić alert — dla każdego.
		path: p(Routing.PanelAdvisor),
		icon: 'assistant',
		label: 'Doradca',
		labelKey: 'advisor.nav',
		module: 'advisor',
	},
	{
		path: p(Routing.PanelMyTrades),
		icon: 'swap_horiz',
		label: 'Moje transakcje',
		labelKey: 'advisor.navUser',
		module: 'advisor',
	},
	{
		path: p(Routing.PanelMonitor),
		icon: 'insights',
		label: 'Monitor',
		labelKey: 'monitor.nav',
		module: 'sentiment',
	},
	{
		path: p(Routing.PanelPortfolio),
		icon: 'account_balance_wallet',
		label: 'Portfolio',
		module: 'portfolio',
	},
	{
		path: p(Routing.PanelKlimat),
		icon: 'thermostat',
		label: 'Klimat rynku',
		labelKey: 'nav.klimat',
		prefix: '/market-climate',
		module: 'climate',
	},
	{
		path: p(Routing.PanelConsolidations),
		icon: 'crop_free',
		label: 'Konsolidacje',
		labelKey: 'nav.consolidations',
		prefix: '/consolidations',
		module: 'consolidations',
	},
	{
		path: p(Routing.PanelCatchTrend),
		icon: 'rocket_launch',
		label: 'Radar trendu',
		labelKey: 'nav.catchTrend',
		prefix: '/trend-radar',
		module: 'catch_trend',
	},

	{
		path: p(Routing.PanelGainsMonitor),
		icon: 'trending_up',
		label: 'Monitor wzrostów',
		prefix: '/gains-monitor',
		module: 'gains_monitor',
	},

	{
		path: p(Routing.PanelDailyRange),
		icon: 'date_range',
		label: 'Zakres dnia',
		labelKey: 'nav.dailyRange',
		prefix: '/daily-range',
		module: 'daily_range',
	},

	{
		path: p(Routing.PanelOverbalance),
		icon: 'balance',
		label: 'Overbalance',
		labelKey: 'nav.overbalance',
		prefix: '/overbalance',
		module: 'overbalance',
	},

	{ path: p(Routing.PanelWatchlist), icon: 'star', label: 'Watchlist', module: 'portfolio' },
	{
		path: p(Routing.PanelExchangeAccounts),
		icon: 'account_balance',
		label: 'Exchange',
		labelKey: 'exchange.navLabel',
		prefix: '/exchange',
		module: 'exchange',
	},
	{
		path: p(Routing.PanelBtcAnalytics),
		icon: 'currency_bitcoin',
		label: 'BTC Analytics',
		module: 'btc',
	},
	{
		path: p(Routing.PanelSrZones),
		icon: 'stacked_line_chart',
		label: 'Strefy S/R',
		labelKey: 'nav.srZones',
		prefix: '/sr',
		module: 'sr_zones',
	},
	{
		path: p(Routing.PanelAnalytics360),
		icon: 'radar',
		label: 'Analityka 360',
		labelKey: 'nav.analytics360',
		module: 'analytics360',
	},
	{ path: p(Routing.PanelVolume), icon: 'waterfall_chart', label: 'Volume', module: 'volume' },
	{
		path: p(Routing.PanelOil),
		icon: 'oil_barrel',
		label: 'Oil',
		labelKey: 'oil.nav',
		module: 'oil',
	},
	{
		// Auto Doradca: portfel próbny strategii, sam podejmuje decyzje.
		path: p(Routing.PanelAutoAdvisor),
		icon: 'smart_toy',
		label: 'Auto Doradca',
		labelKey: 'advisor.navAuto',
		adminOnly: true,
	},
	{
		// Quant: rejestr przewag i werdykty hipotez — metoda, więc tylko admin.
		path: p(Routing.PanelQuant),
		icon: 'science',
		label: 'Quant',
		labelKey: 'nav.quant',
		adminOnly: true,
	},
	{
		path: p(Routing.PanelSocial),
		icon: 'campaign',
		label: 'Social Media',
		labelKey: 'nav.social',
		adminOnly: true,
	},
	{
		path: p(Routing.PanelPresentations),
		icon: 'slideshow',
		label: 'Prezentacje',
		labelKey: 'nav.presentations',
		adminOnly: true,
	},
	{
		path: p(Routing.PanelAdminUsers),
		icon: 'group',
		label: 'Users',
		labelKey: 'adminUsers.nav',
		prefix: '/admin/users',
		adminOnly: true,
	},
	{
		path: p(Routing.PanelAdmin),
		icon: 'admin_panel_settings',
		label: 'Admin Panel',
		labelKey: 'nav.admin',
		adminOnly: true,
	},
];

/**
 * Boundary-aware active-state matcher (avoids `/monitor` matching `/monitoring/...`).
 * Returns true when `url` is exactly the item's path/prefix or a child of it.
 */
export function navItemMatches(item: NavItem, url: string): boolean {
	const base = item.prefix ?? item.path;
	// Query/fragment are not part of the route (`/notifications?view=feed` is still Powiadomienia).
	const path = url.split(/[?#]/)[0];
	return path === base || path.startsWith(`${base}/`);
}

/**
 * Resolve the gated module for a target URL by finding the matching `NavItem`.
 * Returns `undefined` when the route is ungated (no matching item, or the item
 * has no `module`) — the guard then lets it through.
 */
export function moduleForUrl(url: string): ModuleId | undefined {
	// Strip query/fragment before matching.
	const path = url.split(/[?#]/)[0];
	const item = CMS_NAV_ITEMS.find((i) => navItemMatches(i, path));
	return item?.module;
}
